import { useState } from 'react';

import { InternalQuestion, InternalQuestionGroups, useQuestionContext, useSubmissionContext } from '../../../contexts';
import { AnswerInput, QuestionType } from '../../../graphqlTypes';
import { isDateValid } from '../../../utils';
import { SelectType } from '../Select';

export const NO_ISSUES_CODE = 'no_issues';

export const IMPLEMENTED_QUESTIONS = [
  QuestionType.singleselect,
  QuestionType.multiselect,
  QuestionType.singlemultiselect,
  QuestionType.gallery,
  QuestionType.units,
  QuestionType.currency,
  QuestionType.note,
  QuestionType.media,
  QuestionType.medianote,
  QuestionType.date,
  'address',
];

export const isMediaQuestion = (question: InternalQuestion) => {
  return (
    question.type === QuestionType.gallery ||
    question.type === QuestionType.media ||
    question.type === QuestionType.medianote
  );
};

interface useQuestionProps {
  Alert: (title: string, message?: string, buttons?: any[], options?: any, onConfirm?: any) => void;
  t: (k: string, options?: any) => string;
  locale: string;
  isEditingQuestionGroup: boolean;
  setIsEditingQuestionGroup: (value: boolean) => void;
}
export const useQuestion = ({
  Alert,
  t,
  locale,
  isEditingQuestionGroup,
  setIsEditingQuestionGroup,
}: useQuestionProps) => {
  const {
    activeQuestion,
    answerQuestion,
    activateNextQuestion,
    questionGroup,
    questions,
    reEvaluateDisplayRules,
    evalValidationRule,
  } = useQuestionContext();
  const {
    activateQuestionGroup,
    answerQuestionGroup,
    activateLast,
    lastActiveQuestionGroup,
    discardQuestionGroup,
    isEditingSubmission,
    questionGroupBeingEdited,
    submittingStructure,
    reEvaluateQuestionGroupsDisplayRules,
    isReadyForSubmit,
    deactivateQuestionGroups,
    requireContinueConfirmation,
    preloadedData,
    allQuestionAnswered,
  } = useSubmissionContext();

  const [goToNext, setGoToNext] = useState(
    allQuestionAnswered(questionGroup) && !isEditingQuestionGroup ? true : false,
  );

  const hasPreloadedAnswerNotEditable =
    preloadedData?.answers &&
    Object.keys(preloadedData?.answers)?.includes(activeQuestion?.key) &&
    !questionGroup.editable;

  const nonEditable =
    (isEditingSubmission && !questionGroup.editable) ||
    hasPreloadedAnswerNotEditable ||
    (questionGroup.answerOnlyOnce && questionGroup.answered === true) ||
    !activeQuestion;

  const animateQuestion = async (
    questionGroup: InternalQuestionGroups,
    answer: AnswerInput,
    enforceAnimation?: boolean,
  ) => {
    const allowedAnimationTypes = [QuestionType.singleselect, QuestionType.multiselect, QuestionType.singlemultiselect];
    if (
      (questionGroup?.questions?.length === 1 || enforceAnimation) &&
      allowedAnimationTypes.includes(questionGroup?.questions?.[0]?.type)
    ) {
      const question = questionGroup?.questions?.[0];
      const questionDiv = document.getElementsByClassName(`question-container-${question.key}`)?.[0];
      // Sum 1 because the first question is the title
      const answerIndex = question?.options?.findIndex((option) => option.value === answer?.[0]?.value) + 1;
      const answerDiv = questionDiv?.children[answerIndex];
      answerDiv?.classList.add('flash-animation');

      await new Promise((resolve) => {
        setTimeout(() => {
          answerDiv?.classList.remove('flash-animation');
          resolve('true');
        }, 600);
      });
    }
  };

  const onNext = async () => {
    /**
     * If question answer is optional
     * use [] to indicate answered as 'empty'
     * Use undefined for not answered yet
     **/
    if (activeQuestion?.tempAnswer === undefined && !activeQuestion?.requiresAnswer) {
      updateTempAnswer([], activeQuestion);
    }

    const questionAnswerValidation = activeQuestion ? evalValidationRule(activeQuestion) : { error: false, msg: '' };

    if (!questionAnswerValidation.error) {
      const isThereNextQuestion = activateNextQuestion();

      if (!isThereNextQuestion) {
        const isNoIssuesTempAnswer = activeQuestion?.tempAnswer?.[0]?.code === NO_ISSUES_CODE;

        await animateQuestion(questionGroup, activeQuestion?.tempAnswer, isNoIssuesTempAnswer);
        answerQuestionGroup(questionGroup);

        if (questionGroup.requiresConfirmation) {
          deactivateQuestionGroups();
          requireContinueConfirmation(true);
        } else {
          activateLast();
          reEvaluateQuestionGroupsDisplayRules();
        }
        setIsEditingQuestionGroup(false);
      }
    } else {
      Alert(questionAnswerValidation.msg);
    }
  };

  const isSelectQuestion = (question: InternalQuestion) =>
    question?.type === 'singleselect' || question?.type === 'multiselect' || question?.type === 'singlemultiselect';

  const updateTempAnswer = (answer: any, question?: InternalQuestion) => {
    if (question) {
      answerQuestion(question, answer);
    }
    if (isEditingQuestionGroup || question !== activeQuestion || isSelectQuestion(question)) {
      // If in editing mode (1), or when a previous answered question has changed (2), or if the question is of
      // type 'select' and its response might alter the next questions that must be rendered (3),
      // we want to answer the question and evaluate the display rules
      reEvaluateDisplayRules();
    }
  };

  const onDiscard = () => {
    discardQuestionGroup(questionGroup);
    activateLast();
    setIsEditingQuestionGroup(false);
    reEvaluateDisplayRules();
  };

  const onEdit = () => {
    const editQuestionBlock = () => {
      activateQuestionGroup(questionGroup, true);
      setIsEditingQuestionGroup(true);
    };

    const areChangesNotSaved = () => {
      let changesNotSaved = false;

      try {
        changesNotSaved = !questionGroupBeingEdited.questions.every((question) => {
          let questionHasNotChanged: boolean;

          switch (question.type) {
            case 'units':
            case 'currency': {
              questionHasNotChanged =
                question.answer?.[0]?.extra[0] === question.tempAnswer?.extra &&
                question.answer?.[0]?.value === question.tempAnswer?.value;
              return questionHasNotChanged;
            }
            case 'note': {
              questionHasNotChanged = question.answer?.[0]?.value === question.tempAnswer?.value;
              return questionHasNotChanged;
            }
            case 'gallery':
            case 'media': {
              if (
                (!question.answer && !question.tempAnswer) ||
                (question.answer.length === 0 && question.tempAnswer.length === 0)
              ) {
                questionHasNotChanged = true;
              } else {
                // Check answer and tempAnswer arrays for differences
                const answerParsed = question.answer?.reduce((pictures, answer) => {
                  if (!answer?.value) {
                    return;
                  }
                  pictures.push({
                    url: answer.value,
                    thumbnail: answer.value,
                    uploading: false,
                    failed: false,
                    questionId: question.key,
                    placeholderName: answer.extra?.[0],
                  });
                  return pictures;
                }, []);
                questionHasNotChanged = JSON.stringify(answerParsed) === JSON.stringify(question.tempAnswer);
              }
              return questionHasNotChanged;
            }
            case 'singleselect':
            case 'multiselect':
            case 'singlemultiselect': {
              // 1. Check length of answer and tempAnswer.
              questionHasNotChanged = question.answer?.length === question.tempAnswer?.length;
              if (questionHasNotChanged) {
                // 2. For each answer and tempAnswer, check that no changes were made.
                for (let i = 0; i < question.answer?.length; i += 1) {
                  questionHasNotChanged = JSON.stringify(question.answer[i]) === JSON.stringify(question.tempAnswer[i]);
                  if (!questionHasNotChanged) break;
                }
              }
              return questionHasNotChanged;
            }
            default: {
              return true;
            }
          }
        });
      } catch (error: any) {
        changesNotSaved = true;
      }

      return changesNotSaved;
    };

    if (questionGroupBeingEdited && areChangesNotSaved()) {
      Alert(
        '',
        t('submission.unsavedChanges', { question: questionGroupBeingEdited?.title }),
        [
          {
            text: t('buttons.discardChanges'),
            onPress: () => {
              discardQuestionGroup(questionGroupBeingEdited);
              editQuestionBlock();
            },
          },
          {
            text: t('buttons.stay'),
          },
        ],
        undefined,
        () => {
          discardQuestionGroup(questionGroupBeingEdited);
          editQuestionBlock();
        },
      );
    } else {
      editQuestionBlock();
    }
  };

  const getSelectType = (questionType: QuestionType) => {
    switch (questionType) {
      case QuestionType.singleselect:
        return SelectType.single;
      case QuestionType.multiselect:
        return SelectType.multi;
      case QuestionType.singlemultiselect:
        return SelectType.singleMulti;
    }
  };

  const handleQuestionEdit = () => {
    if (nonEditable) return;

    if (!questionGroup.active) {
      onEdit();
    }
  };

  if (goToNext) {
    setGoToNext(false);
    void onNext();
  }

  const totalVisibleQuestions = questions?.reduce((count, question) => (question.visible ? count + 1 : count), 0) || 0;

  const isValidAnswer = (answer: any, question: InternalQuestion): boolean => {
    const questionType = question?.type;
    const answerValue = answer?.value as string;
    let isValid = false;

    switch (questionType) {
      case 'address' as QuestionType:
      case QuestionType.units:
      case QuestionType.currency:
      case QuestionType.note:
        isValid = answerValue?.length > 0;
        break;
      case QuestionType.medianote: {
        const isNoteValid = answerValue?.length > 0;
        const isPicturesValid = answer?.pictures?.length > 0;
        isValid = isNoteValid || isPicturesValid;
        break;
      }
      case QuestionType.date:
        isValid = isDateValid(answerValue, locale);
        break;
      default:
        isValid = answer?.length > 0;
    }

    return isValid;
  };

  const isNextButtonDisabled = () => {
    let isDisabled = true;

    for (const question of questionGroup.questions) {
      isDisabled = question.requiresAnswer && !isValidAnswer(question.tempAnswer, question) && question.visible;
      if (isDisabled) break;
    }

    return isDisabled && IMPLEMENTED_QUESTIONS.includes(activeQuestion?.type);
  };

  return {
    submittingStructure,
    getSelectType,
    handleQuestionEdit,
    onDiscard,
    onEdit,
    onNext,
    isNextButtonDisabled,
    totalVisibleQuestions,
    updateTempAnswer,
    questionGroup,
    lastActiveQuestionGroup,
    activeQuestion,
    nonEditable,
    isReadyForSubmit,
    questions,
    setGoToNext,
  };
};
